import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { MdsIcon } from "@/design-system/components/icon/MdsIcon";
import { MdsIconKind } from "@/design-system/components/icon";
import {
  FloatingNotePreview,
  HEADS_UP_PREVIEW_PLACEMENT,
} from "@/design-system/components/item-list/rows/item-preview/FloatingNotePreview";
import { HoveringState } from "@/design-system/components/item-list/rows/item-preview/HoveringState";
import { MDSText, MDSTextColor, MDSTextOverflow, MDSTextVariant, MDSTextWeight } from "@/mds";
import type { FC } from "react";
import { useHeadsUpContext } from "@/components/heads-up/context/heads-up-context";
import { observer } from "mobx-react-lite";
import type { Maybe } from "@/domains/common/types";
import type { SmartNotePreviewInfo } from "./smart-note-preview.types";
import { getHeadsUpSidebarAnchorRect } from "./get-heads-up-sidebar-anchor";
import type { INoteObservable } from "@/store/note/types";

export interface SmartNoteRowProps {
  noteId: string;
  primary: string;
  secondary?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
}

export const SmartNoteRow: FC<SmartNoteRowProps> = observer(
  ({ noteId: _noteId, primary, secondary, onClick, notePreviewInfo }) => {
    const hoverState = useMemo(() => new HoveringState(), []);
    const {
      resources: { loadNote },
    } = useHeadsUpContext();

    return (
      <SmartNoteRowWrapper>
        <NoteRow
          onClick={onClick}
          onMouseEnter={event => {
            const hydrationPromise = _noteId ? loadNote(_noteId) : undefined;

            if (notePreviewInfo && !notePreviewInfo.isNoteContentDocumentLoaded) {
              void notePreviewInfo.fetchNoteContentDocument();
            }

            if (hydrationPromise) {
              hydrationPromise
                .then((loaded: Maybe<INoteObservable>) => {
                  if (loaded && !loaded.isNoteContentDocumentLoaded) {
                    void loaded.fetchNoteContentDocument();
                  }
                })
                .catch(() => {});
            }

            hoverState.hoveredRect = getHeadsUpSidebarAnchorRect(
              event.currentTarget as HTMLElement
            );
            hoverState.handleMouseEnter();
          }}
          onMouseLeave={() => {
            hoverState.hoveredRect = null;
            hoverState.handleMouseLeave();
          }}
        >
          <LeadingIcon $isDoubleRow={!!secondary}>
            <MdsIcon kind={MdsIconKind.Document} />
          </LeadingIcon>
          <NoteContent>
            <NoteTitle
              title={primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Regular}
              overflow={MDSTextOverflow.Ellipsis}
              numberOfLines={1}
            >
              {primary}
            </NoteTitle>
            {secondary ? (
              <NoteSubtitle
                title={secondary}
                variant={MDSTextVariant.TextXs}
                color={MDSTextColor.Secondary}
                overflow={MDSTextOverflow.Ellipsis}
              >
                {secondary}
              </NoteSubtitle>
            ) : null}
          </NoteContent>
        </NoteRow>

        {hoverState.hovering && notePreviewInfo?.noteObservable && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={notePreviewInfo.noteObservable}
            placement={HEADS_UP_PREVIEW_PLACEMENT}
          />
        )}
      </SmartNoteRowWrapper>
    );
  }
);

const SmartNoteRowWrapper = styled.div`
  position: relative;
`;

export const NoteRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xsPlus};
  min-height: 32px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.background};

  background-color: ${({ theme }) => theme.colors.background.button.transparent.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
`;

export const LeadingIcon = styled.div<{ $isDoubleRow: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: ${({ $isDoubleRow }) => ($isDoubleRow ? "2px" : "-1px")};
  margin-right: -2px;

  & svg {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.icon.secondary};
  }
`;

const NoteContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const NoteTitle = styled(MDSText)`
  white-space: nowrap;
`;

export const NoteSubtitle = styled(MDSText)``;
